import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, Minus, Plus, ChevronDown, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useRegion } from '../context/RegionContext';
import { useProducts } from '../hooks/useProducts';
import ReviewSection from '../components/products/ReviewSection';
import ProductCard from '../components/products/ProductCard';
import { furnitureColors, getColorById } from '../data/furnitureColors';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { formatPrice } = useRegion();
  
  // Fetch products from JSON
  const { products, loading, error, getProductById } = useProducts();
  
  const product = getProductById(productId);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [showColorError, setShowColorError] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  
  // Custom dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHandleDropdownOpen, setIsHandleDropdownOpen] = useState(false);
  const [selectedHandle, setSelectedHandle] = useState(null);
  const dropdownRef = useRef(null);
  const handleDropdownRef = useRef(null);

  // Get similar products based on category, style, or room
  const getSimilarProducts = () => {
    if (!product) return [];

    return products
      .filter(p => 
        p.id !== product.id && 
        (
          p.category === product.category ||
          p.style === product.style ||
          p.room === product.room ||
          p.material === product.material
        )
      )
      .slice(0, 8);
  };

  const similarProducts = getSimilarProducts();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (handleDropdownRef.current && !handleDropdownRef.current.contains(event.target)) {
        setIsHandleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lightbox keyboard navigation
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      if (e.key === 'ArrowRight' && product.images && product.images.length > 1) {
        setSelectedImage((prev) => (prev + 1) % product.images.length);
      }
      if (e.key === 'ArrowLeft' && product.images && product.images.length > 1) {
        setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, product]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading product...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading product: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Product not found
  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-dark-900 mb-4">Product Not Found</h2>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/shop')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    if (!selectedColor) {
      setShowColorError(true);
      document.getElementById('color-selector')?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      return;
    }

    // Prepare cart item data
    const cartItemData = {
      color: selectedColor === 'same-as-image' ? 'Same as in image' : getColorById(selectedColor).name,
      colorId: selectedColor === 'same-as-image' ? 'same-as-image' : getColorById(selectedColor).id,
      colorHex: selectedColor === 'same-as-image' ? '#808080' : getColorById(selectedColor).hex
    };

    // Add handle option if selected
    if (selectedHandle) {
      cartItemData.handle = selectedHandle === 'same-as-image' ? 'Same as in image' : selectedHandle;
    }

    addToCart(product, quantity, cartItemData);
    setShowColorError(false);
  };

  const handleColorChange = (colorId) => {
    setSelectedColor(colorId);
    setShowColorError(false);
    setIsDropdownOpen(false);
  };

  const handleHandleChange = (handleNumber) => {
    setSelectedHandle(handleNumber);
    setIsHandleDropdownOpen(false);
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  // Lightbox functions
  const openLightbox = () => setIsLightboxOpen(true);
  const closeLightbox = () => setIsLightboxOpen(false);
  
  const nextImage = () => {
    if (product.images && product.images.length > 1) {
      setSelectedImage((prev) => (prev + 1) % product.images.length);
    }
  };

  const previousImage = () => {
    if (product.images && product.images.length > 1) {
      setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  // Get display text for selected color
  const getSelectedColorDisplay = () => {
    if (!selectedColor) return '-- Choose a Color --';
    if (selectedColor === 'same-as-image') return 'Same as in image';
    return getColorById(selectedColor).name;
  };

  // Get display text for selected handle
  const getSelectedHandleDisplay = () => {
    if (!selectedHandle) return '-- Choose a Handle --';
    if (selectedHandle === 'same-as-image') return 'Same as in image';
    return `Handle ${selectedHandle}`;
  };

  // Generate handle numbers array (01-36)
  const handleNumbers = Array.from({ length: 36 }, (_, i) => {
    const num = i + 1;
    return num < 10 ? `0${num}` : `${num}`;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* LIGHTBOX MODAL */}
      {isLightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4 animate-fade-in"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors shadow-lg"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-gray-800" />
          </button>

          {/* Image Counter */}
          {product.images && product.images.length > 1 && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm font-medium">
              {selectedImage + 1} / {product.images.length}
            </div>
          )}

          {/* Previous Button */}
          {product.images && product.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                previousImage();
              }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-white rounded-full hover:bg-gray-100 transition-all shadow-lg hover:scale-110"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6 text-gray-800" />
            </button>
          )}

          {/* Main Image */}
          <div 
            className="max-w-7xl max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={product.images ? product.images[selectedImage] : product.image}
              alt={`${product.name} - Full view`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>

          {/* Next Button */}
          {product.images && product.images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-white rounded-full hover:bg-gray-100 transition-all shadow-lg hover:scale-110"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6 text-gray-800" />
            </button>
          )}

          {/* Instructions */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm">
            Press ESC to close • Use arrow keys to navigate
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm">
            <button 
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-primary-600 transition-colors"
            >
              Home
            </button>
            <span className="text-gray-400">/</span>
            <button 
              onClick={() => navigate('/shop')}
              className="text-gray-600 hover:text-primary-600 transition-colors"
            >
              Shop
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium truncate">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Product Details Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Images */}
            <div>
              {/* Main Image*/}
              <div 
                className="aspect-square rounded-xl overflow-hidden bg-gray-100 mb-4 relative group cursor-pointer"
                onClick={openLightbox}
              >
                <img
                  src={product.images ? product.images[selectedImage] : product.image}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                
                {/* Zoom Icon Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                  <div className="bg-white rounded-full p-3 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300 shadow-lg">
                    <ZoomIn className="w-6 h-6 text-gray-800" />
                  </div>
                </div>

                {/* "Click to enlarge" text */}
                <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  Click to enlarge
                </div>
              </div>
              
              {/* Thumbnail Images */}
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                        selectedImage === index
                          ? 'border-primary-600 scale-105 shadow-md'
                          : 'border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-display font-bold text-dark-900 mb-4">
                {product.name}
              </h1>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                <p className="text-3xl md:text-4xl font-medium font-bold text-primary-700">
                  {formatPrice(product.priceCAD)}
                </p>
                {product.inStock ? (
                  <span className="text-sm text-accent-600 font-semibold">In Stock</span>
                ) : (
                  <span className="text-sm text-red-600 font-semibold">Out of Stock</span>
                )}
              </div>

              {/* Description */}
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {product.description}
              </p>

              {/* CUSTOM COLOR DROPDOWN SECTION */}
              <div id="color-selector" className="mb-6 scroll-mt-24">
                <div className="border-t pt-6">
                  {/* Label */}
                  <label className="block text-lg font-bold text-dark-900 mb-4">
                    Select Color
                  </label>

                  {/* Custom Dropdown */}
                  <div className="relative" ref={dropdownRef}>
                    {/* Dropdown Button */}
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full py-3 px-4 pr-10 text-base font-medium border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white cursor-pointer transition-all duration-200 hover:border-primary-400 text-left"
                    >
                      {getSelectedColorDisplay()}
                    </button>

                    {/* Chevron Icon */}
                    <ChevronDown 
                      className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none transition-transform duration-200 ${
                        isDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl max-h-[500px] overflow-y-auto">
                        {/* Reference Image at the Top - Full & Scrollable */}
                        <div className="p-3 border-b-2 border-gray-200 bg-gray-50">
                          <p className="text-sm text-gray-700 mb-2 font-semibold text-center">
                            Color Reference Guide
                          </p>
                          <img 
                            src="/images/colorOptions/color-reference.jpeg" 
                            alt="Color Reference Guide"
                            className="w-full h-auto rounded shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.previousElementSibling.textContent = 'Reference image not available';
                            }}
                          />
                        </div>

                        {/* Color Options - Text Only */}
                        <div className="py-1">
                          {/* Same as in image option */}
                          <button
                            type="button"
                            onClick={() => handleColorChange('same-as-image')}
                            className={`w-full px-4 py-3 text-left hover:bg-primary-50 transition-colors ${
                              selectedColor === 'same-as-image' ? 'bg-primary-100 font-semibold' : ''
                            }`}
                          >
                            Same as in image
                          </button>

                          {/* Furniture Colors - Text Only */}
                          {furnitureColors.map((color) => (
                            <button
                              key={color.id}
                              type="button"
                              onClick={() => handleColorChange(color.id)}
                              className={`w-full px-4 py-3 text-left hover:bg-primary-50 transition-colors ${
                                selectedColor === color.id ? 'bg-primary-100 font-semibold' : ''
                              }`}
                            >
                              {color.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Validation Error Message */}
                  {showColorError && !selectedColor && (
                    <div className="mt-4 p-3 bg-red-50 border-2 border-red-200 rounded-lg">
                      <p className="text-red-700 font-medium text-sm">
                        ⚠️ Please select a color before adding to cart
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* CUSTOM HANDLE OPTIONS DROPDOWN SECTION */}
              <div id="handle-selector" className="mb-6">
                <div className="border-t pt-6">
                  {/* Label */}
                  <label className="block text-lg font-bold text-dark-900 mb-4">
                    Custom Handle Options
                  </label>

                  {/* Custom Dropdown */}
                  <div className="relative" ref={handleDropdownRef}>
                    {/* Dropdown Button */}
                    <button
                      type="button"
                      onClick={() => setIsHandleDropdownOpen(!isHandleDropdownOpen)}
                      className="w-full py-3 px-4 pr-10 text-base font-medium border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white cursor-pointer transition-all duration-200 hover:border-primary-400 text-left"
                    >
                      {getSelectedHandleDisplay()}
                    </button>

                    {/* Chevron Icon */}
                    <ChevronDown 
                      className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none transition-transform duration-200 ${
                        isHandleDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />

                    {/* Dropdown Menu */}
                    {isHandleDropdownOpen && (
                      <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl max-h-[500px] overflow-y-auto">
                        {/* Handle Reference Image at the Top */}
                        <div className="p-3 border-b-2 border-gray-200 bg-gray-50">
                          <p className="text-sm text-gray-700 mb-2 font-semibold text-center">
                            Custom Handle Options
                          </p>
                          <img 
                            src="/images/handleOptions/handle-reference.jpeg" 
                            alt="Custom Handle Options"
                            className="w-full h-auto rounded shadow-sm"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.previousElementSibling.textContent = 'Reference image not available';
                            }}
                          />
                        </div>

                        {/* Handle Options Grid */}
                        <div className="p-4">
                          {/* "Same as in image" button - Full width on mobile, col-span-2 on desktop */}
                          <button
                            type="button"
                            onClick={() => handleHandleChange('same-as-image')}
                            className={`w-full mb-3 py-3 px-3 text-center font-semibold text-sm sm:text-base rounded-lg border-2 transition-all duration-200 ${
                              selectedHandle === 'same-as-image'
                                ? 'bg-primary-600 text-white border-primary-600 shadow-md scale-105' 
                                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                            }`}
                          >
                            Same as in image
                          </button>

                          {/* Handle Numbers Grid - 5 cols on mobile, 6 cols on desktop */}
                          <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
                            {handleNumbers.map((number) => (
                              <button
                                key={number}
                                type="button"
                                onClick={() => handleHandleChange(number)}
                                className={`py-3 sm:py-2.5 px-2 text-center font-semibold text-sm rounded-lg border-2 transition-all duration-200 ${
                                  selectedHandle === number 
                                    ? 'bg-primary-600 text-white border-primary-600 shadow-md scale-105' 
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                                }`}
                              >
                                {number}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Clear Selection Option */}
                        <div className="border-t-2 border-gray-200 p-2">
                          <button
                            type="button"
                            onClick={() => handleHandleChange(null)}
                            className="w-full py-2 text-center text-sm text-gray-600 hover:text-primary-600 hover:bg-gray-50 rounded transition-colors font-medium"
                          >
                            Clear Selection
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Product Dimensions Info */}
              {product.dimensions && (
                <div className="mb-6">
                  <div className="border-t pt-4">
                    {/* Two-column grid: Product Dimensions and Custom Size */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                      {/* Product Dimensions Section */}
                      <div className="flex flex-col">
                        <h3 className="text-lg font-bold text-dark-900 mb-3">Product Dimensions</h3>
                        <div className="bg-gray-100 rounded-lg py-3 px-4 text-center flex-1 flex flex-col justify-center">
                          <p className="text-sm text-gray-600 mb-1">Height × Width × Depth</p>
                          <p className="text-lg font-semibold text-dark-900">
                            {product.dimensions.height} × {product.dimensions.width} × {product.dimensions.depth}
                          </p>
                        </div>
                      </div>
                      {/* Custom Size Section */}
                      <div className="flex flex-col">
                        <h3 className="text-lg font-bold text-dark-900 mb-3">Custom size</h3>
                        <div className="bg-gray-100 rounded-lg py-3 px-4 flex items-center justify-center flex-1">
                          <Link
                            to="/contact"
                            className="text-primary-600 h-[52px] flex items-center justify-center text-center font-semibold hover:underline text-base w-full"
                          >
                            Click Here For Custom Quote
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-dark-900 mb-2">
                  Quantity
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border-2 border-gray-300 rounded-lg">
                    <button
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                      className="p-3 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-6 font-semibold text-lg">{quantity}</span>
                    <button
                      onClick={incrementQuantity}
                      className="p-3 hover:bg-gray-100 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  className="flex-1 flex items-center justify-center gap-3 px-8 py-4 bg-primary-600 text-white text-lg font-semibold rounded-xl hover:bg-primary-700 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-primary-600"
                >
                  <ShoppingCart className="w-6 h-6" />
                  {product.inStock ? 'Add to Cart' : 'Out of Stock'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SIMILAR PRODUCTS SECTION */}
        {similarProducts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-3xl font-display font-bold text-dark-900 mb-2">
                  You May Also Like
                </h2>
                <p className="text-gray-600">
                  Similar products based on style, category, and room
                </p>
              </div>
            </div>

            {/* Similar Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
              {similarProducts.map((similarProduct) => (
                <ProductCard
                  key={similarProduct.id}
                  product={similarProduct}
                  onQuickView={() => {}}
                />
              ))}
            </div>

            {/* View All Button */}
            <div className="text-center pt-4 border-t">
              <button
                onClick={() => navigate('/products')}
                className="px-8 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                View All Products
              </button>
            </div>
          </div>
        )}

        {/* REVIEWS SECTION */}
        <div id="reviews" className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <ReviewSection productId={product.id} />
        </div>

      </div>
    </div>
  );
};

export default ProductDetailPage;